import { motion } from "framer-motion";
import React, { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import "../customCSS/AnimatedCartButton.css";

import { addToCartAPI, removeFromCartAPI } from "../api/cartApiService";
import { useCart } from "../hooks/useCart";
import { setCartCount } from "../store/apiCountsSlice";
import { openAuthModal, openCartDrawer } from "../store/uiSlice";
import { getUserType } from "../utilities/cookieUtils";

interface AnimatedCartButtonProps {
  mappingCode: string;
  variationCode: string;
  qty?: number;
  availableStock?: number;
  onAdded?: () => void;
  onRemoved?: () => void;
  initialInCart?: boolean;
  disabled?: boolean;
}

const AnimatedCartButton: React.FC<AnimatedCartButtonProps> = ({
  mappingCode,
  variationCode,
  qty = 1,
  availableStock,
  onAdded,
  onRemoved,
  initialInCart,
  disabled = false,
}) => {
  const { isInCart, addToCart, removeFromCart } = useCart();
  const dispatch = useDispatch();
  const userInfo = useSelector((state: any) => state.auth.userInfo);

  // faster performance: memoized check
  const inCart = useMemo(() => isInCart(mappingCode), [isInCart, mappingCode]);

  // local display state to allow initial value from API flags
  const [displayInCart, setDisplayInCart] = React.useState<boolean>(
    typeof initialInCart === "boolean" ? initialInCart : inCart,
  );

  // Sync display state with global cart state (only after initial load)
  React.useEffect(() => {
    // Trust API response on initial load, then sync with global state on changes
    if (typeof initialInCart !== "boolean") {
      setDisplayInCart(inCart);
    }
  }, [inCart]);

  const [hovering, setHovering] = useState(false);
  const [animating, setAnimating] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleAdd = async () => {
    if (isLoading) return; // Prevent spam

    setIsLoading(true);
    setAnimating(true);

    // Check if user is logged in or guest
    const isLoggedIn = !!userInfo?.user_id || !!userInfo?.guest_id;
    if (!isLoggedIn) {
      // Guest: update cookie only
      addToCart(mappingCode, qty);
      setDisplayInCart(true);
      setAnimating(false);
      setIsLoading(false);
      onAdded?.();
      return;
    }

    // Logged in: send API
    const result = await addToCartAPI(mappingCode, variationCode, qty);

    setTimeout(() => {
      if (result.success && result.messageCode === 100) {
        setDisplayInCart(true);
        if (result.count !== undefined) {
          dispatch(setCartCount(result.count));
        }
        onAdded?.();
      }
      setAnimating(false);
      setIsLoading(false);
    }, 600);
  };

  const handleRemove = async () => {
    if (isLoading) return; // Prevent spam

    Swal.fire({
      title: "Remove from Cart?",
      text: "Do you want to remove this item from your cart?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, remove",
    }).then(async (result) => {
      if (result.isConfirmed) {
        setIsLoading(true);

        const isLoggedIn = !!userInfo?.user_id || !!userInfo?.guest_id;
        if (!isLoggedIn) {
          removeFromCart(mappingCode);
          setDisplayInCart(false);
          setIsLoading(false);
          onRemoved?.();
          return;
        }

        // Logged in: call API
        const apiResult = await removeFromCartAPI(mappingCode);
        if (apiResult.success && apiResult.messageCode === 100) {
          setDisplayInCart(false);
          if (apiResult.count !== undefined) {
            dispatch(setCartCount(apiResult.count));
          }
          onRemoved?.();
        }
        setIsLoading(false);
      }
    });
  };

  // If availableStock is provided and 0, disable add to cart
  const isOutOfStock = qty === 0 || availableStock === 0;
  // Treat parent `disabled` prop as an out-of-stock/disabled indicator for UI
  const showOutOfStock = Boolean(isOutOfStock || disabled);

  const handleClick = () => {
    if (isLoading || showOutOfStock) return;
    const userType = getUserType();
    if(userType === "random") {
      dispatch(openAuthModal());
      return;
    }

    // If item is in cart, open the cart drawer
    if (displayInCart) {
      dispatch(openCartDrawer());
    } else {
      // Otherwise, add to cart
      handleAdd();
    }
  };

  const shakeAnimation = {
    x: [-3, 3, -3, 3, 0],
    transition: { duration: 0.5, repeat: Infinity },
  };

  return (
    <button
      className={`cart-btn-enhanced ${displayInCart ? "cart-btn-in-cart" : "cart-btn-add"}${isLoading || showOutOfStock ? " cart-btn-disabled" : ""}`}
      onClick={isLoading || showOutOfStock ? undefined : handleClick}
      onMouseEnter={() => !isLoading && !showOutOfStock && setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      disabled={isLoading || showOutOfStock}>
      {/* Background gradient effect */}
      <div className="cart-btn-gradient"></div>

      {/* CART ICON */}
      <div className="cart-icon-wrapper">
        {/* Idle icon */}
        {!animating && (
          <motion.i
            className={`${displayInCart ? "bi bi-bag-check-fill" : "bi bi-cart-plus-fill"} cart-icon`}
            animate={hovering && !displayInCart ? shakeAnimation : { x: 0 }}
          />
        )}

        {/* Flying animation */}
        {animating && (
          <motion.i
            className="bi bi-cart-plus-fill cart-icon fly"
            initial={{ x: 0, opacity: 1, scale: 1 }}
            animate={{ x: 100, opacity: 0, scale: 0.5 }}
            transition={{ duration: 0.6, ease: "easeInOut" }}
          />
        )}
      </div>

      {/* BUTTON TEXT */}
      <span className="cart-btn-text">
        {showOutOfStock ? (
          <>Out of Stock</>
        ) : isLoading ? (
          <>
            <span className="spinner-border spinner-border-sm me-2"></span>
            Loading...
          </>
        ) : displayInCart ? (
          <>
            Go to Cart
            <i className="bi bi-arrow-right ms-2"></i>
          </>
        ) : (
          "Add to Cart"
        )}
      </span>
    </button>
  );
};

export default AnimatedCartButton;
